import { toaster } from '@optiaxiom/react';

import { EVENT_NAME } from '@frontend/constants';

import { getPromptQueue } from './promptQueue';

/**
 * Resets the mounted chat body to a brand-new thread and sends `text` as the
 * first user turn.
 *
 * Only meaningful while the chat body is mounted: OPAL_CHAT_SET_THREAD is a
 * transient window event with no listener otherwise, so callers arriving from
 * another route must wait for OPAL_CHAT_READY before calling this.
 *
 * The two steps cannot share a tick, which is the subtle part. The reset
 * dispatch clears `currentThreadAtom` synchronously, but the send path reads
 * the thread it is posting into from its last *committed render*
 * (getOrCreateNewThread returns `currentThread.threadId` when it is set). Drain
 * the queue before React has re-rendered on the cleared atom and the turn is
 * appended to the thread that was open a moment ago — which is why asking from
 * an already-open thread sometimes landed in that thread instead of a new one.
 *
 * So the enqueue is deferred a macrotask. React commits the reset from a
 * MessageChannel task, which precedes a zero-delay timer, so by the time the
 * prompt is queued the body has re-rendered with no thread and reliably creates
 * one. Nothing can undo the reset in between: OPAL_CHAT_SET_THREAD is
 * idempotent for the no-thread case (see ChatEventListener).
 */
export function sendToFreshThread(text: string) {
  // Empty threadId ⇒ the chat starts a new thread (see useSetThreadEvent).
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME.OPAL_CHAT_SET_THREAD, { detail: { threadId: undefined } })
  );
  void new Promise(resolve => setTimeout(resolve, 0))
    .then(getPromptQueue)
    .then(promptQueue =>
      promptQueue().enqueuePrompt({
        type: 'INITIAL_PROMPT',
        // `parts` is not redundant with `content`. The chat body renders a
        // bubble from `parts` and ignores `content` for display, so a turn sent
        // without it shows as an empty bubble for the seconds it takes the
        // server echo to re-sync the message with parts attached.
        payload: [{ content: text, role: 'user', parts: [{ type: 'text', content: text }] }],
      })
    )
    .catch(error => {
      // Covers both a failed enqueue and a failed remote load (the fallback
      // queue rejects). Surface it so the prompt isn't silently dropped after
      // the caller already reset the thread.
      console.error('Failed to enqueue prompt', error);
      toaster.create('Your prompt could not be sent. Please try again.', { intent: 'danger' });
    });
}
